import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'nuova-scheda',
  titolo: 'Nuova scheda e schede da completare',
  gruppo: 'Le schede delle persone',
  permesso: ['persona.crea', 'persona.modifica'],
  aCheServe: 'Come si crea la scheda di una persona nuova e come si completano le schede a cui mancano dati. '
    + 'Il modulo segue l’ordine della Scheda Utente M03.',
  parti: [
    {
      titolo: 'Creare la scheda',
      permesso: 'persona.crea',
      passi: [
        'Prima cerca la persona in «Beneficiari». Se non c’è, premi «Nuova scheda utente».',
        'In «Intestazione» scegli il «Profilo utenti» e la «Sede Servizio». La sede decide quali colleghi '
          + 'vedranno la scheda.',
        'Compila i «Dati anagrafici». Servono cognome, nome e la nascita: in «Precisione della data di '
          + 'nascita» scegli «data completa», «solo l’anno» oppure «non nota» (allora indichi la fascia d’età).',
        'Scrivi il codice fiscale, oppure scegli «Perché non ha il codice fiscale».',
        'Per chi è assistito indica la «Conoscenza della lingua italiana», nella sezione «Lingue».',
        'Compila «Presa visione dell’informativa e consensi» solo se la persona ha già firmato il modulo. '
          + 'Altrimenti li registri dopo, dalla scheda.',
        'Premi «Salva la scheda». Dopo il salvataggio puoi fotografare il documento, oppure premere «Salta e '
          + 'apri la scheda». Per riempire il modulo dalla foto del documento vedi «Compilare dal documento».',
      ],
      immagine: {
        file: 'nuova-scheda.webp', larghezza: 1200, altezza: 760,
        descrizione: 'Il modulo Nuova scheda utente, con l’intestazione e i dati anagrafici.',
      },
    },
    {
      titolo: 'Compilare dal documento',
      permesso: 'persona.crea',
      passi: [
        'In cima alla «Nuova scheda utente», nel riquadro «Compila dal documento», premi «Scatta una foto» oppure '
          + '«Scegli un file»: una foto o un PDF scansionato del documento d’identità.',
        'Controlla che il documento sia intero e leggibile, poi premi «Leggi il documento».',
        'Nella tabella «Dal documento → Nella scheda» sono già spuntati i dati che nel modulo mancano. Togli la '
          + 'spunta ai dati che non vuoi usare e premi «Usa i dati scelti».',
        'I campi riempiti sono evidenziati con «Letto dal documento: controlla». Controllali sul documento, uno per '
          + 'uno, prima di salvare.',
        'Dopo «Salva la scheda» il gestionale propone di caricare lo stesso file tra i documenti della scheda.',
      ],
      immagine: {
        file: 'compila-dal-documento.webp', larghezza: 1200, altezza: 800,
        descrizione: 'Il riquadro Compila dal documento con la tabella Dal documento, Nella scheda e le caselle '
          + 'da spuntare.',
      },
      nota: 'Il documento passa per un servizio di Scaleway a Parigi, che non lo conserva. I dati letti si '
        + 'controllano sempre: niente si salva da solo. Con «Dati anonimizzati» la lettura non è disponibile.',
    },
    {
      titolo: 'Se compare «Esiste già una scheda molto simile.»',
      permesso: 'persona.crea',
      passi: [
        'Apri le schede indicate e controlla.',
        'Se è la stessa persona, premi «Torna al modulo» e lavora sulla scheda che c’è già.',
        'Solo se sei sicuro che è un’altra persona, premi «Salva come persona distinta».',
      ],
    },
    {
      titolo: 'Profilo, codici e lingue',
      testo: [
        'Il «Profilo utenti» è «Migrante o con background migratorio», «Operatore» (chi rappresenta un ente) '
          + 'oppure «CIDIS Dipendenti e Collaboratori» (il personale CIDIS, per la formazione annuale). Per '
          + 'operatori e personale serve l’«Ente di appartenenza» oppure la «Sede CIDIS di appartenenza», mai '
          + 'tutti e due.',
        'STP, ENI, CUI e numeri di ricevuta non vanno nel codice fiscale: vanno nel riquadro «Altri codici» '
          + 'della scheda, con «Aggiungi un codice».',
      ],
    },
    {
      titolo: 'La pagina «Da completare»',
      permesso: 'persona.cerca',
      passi: [
        'Apri la voce di menu «Da completare». In alto leggi quante schede del tuo ambito hanno dati mancanti.',
        'Nel riquadro «Che cosa manca» scegli un dato: l’elenco mostra le schede a cui manca.',
        'Sotto «Altro da sistemare» trovi le «Firme di terzi da completare» e le «Schede da verificare».',
        'Premi «Completa la scheda» nella riga: la scheda si apre con il percorso guidato già avviato.',
      ],
      immagine: {
        file: 'da-completare.webp', larghezza: 1200, altezza: 760,
        descrizione: 'La pagina Da completare con il riquadro Che cosa manca e l’elenco delle schede.',
      },
    },
    {
      titolo: 'Il percorso guidato «Completa la scheda»',
      permesso: 'persona.modifica',
      passi: [
        'Sulla scheda, nell’avviso «Mancano … dati obbligatori», premi «Completa la scheda». Lo stesso fa '
          + '«Modifica la scheda» in testa alla scheda, quando mancano dati.',
        'La finestra «Completa la scheda · 1 di …» chiede un dato alla volta: prima gli obbligatori, poi i '
          + 'consigliati.',
        'Compila e premi «Salva e vai avanti». Ogni passo si salva da solo: se ti fermi non perdi nulla.',
        'Se ora il dato non lo sai, premi «Salta per ora» (o «Salta» sui consigliati).',
        'Alla fine la finestra dice se la scheda è completa o quanti dati restano.',
      ],
      immagine: {
        file: 'completa-la-scheda.webp', larghezza: 1200, altezza: 800,
        descrizione: 'La finestra Completa la scheda, al primo passo, con i comandi Salva e vai avanti e Salta per ora.',
      },
      nota: '«Completa» vuol dire che ci sono tutti i dati obbligatori. I consigliati (per esempio formazione e '
        + 'lavoro) si possono saltare e non cambiano lo stato.',
    },
    {
      titolo: 'Schede «da verificare»',
      permesso: 'persona.modifica',
      testo: [
        'Alcune schede del vecchio archivio hanno la data di nascita o il codice fiscale da controllare. Il '
          + 'segno si toglie da solo quando correggi il dato: spunta «Ho controllato la data su un documento».',
        'Se hai controllato e i dati vanno bene così, premi «Segna come verificata» e scrivi che cosa hai '
          + 'controllato (per esempio «data controllata sul passaporto»).',
      ],
    },
  ],
  avvertenze: [
    {
      quando: '«Questo PDF non contiene una scansione: carica una foto o un PDF scansionato»',
      cosaFare: 'Il PDF è scritto al computer, non è la scansione di un documento. Fotografa il documento con '
        + '«Scatta una foto», oppure scansionalo e carica il PDF della scansione.',
    },
    {
      quando: '«Le cifre di controllo della MRZ non tornano»',
      cosaFare: 'La lettura delle righe con i «<» non è sicura. Confronta con attenzione numero del documento e date '
        + 'con il documento; se puoi, rifai la foto più nitida.',
    },
    {
      quando: '«Codice fiscale non valido (formato non riconosciuto)»',
      cosaFare: 'Nel campo vanno solo il codice fiscale o il codice provvisorio di 11 cifre. STP, ENI, CUI e '
        + 'ricevute vanno in «Altri codici».',
    },
    {
      quando: '«La sede scelta non rientra negli incarichi in corso.»',
      cosaFare: 'Scegli una sede dei tuoi incarichi. Se la persona va seguita da un’altra sede, chiedi a un '
        + 'collega di quella sede.',
    },
    {
      quando: '«Altre schede simili non rientrano negli incarichi in corso»',
      cosaFare: 'Forse la persona è già seguita da un’altra sede. Chiedi a un collega o alla Direzione prima di '
        + 'creare la scheda.',
    },
    {
      quando: '«La scheda è stata creata, ma alcune sezioni non sono state registrate»',
      cosaFare: 'La scheda c’è. Completa le sezioni mancanti dalla linguetta «Anagrafica».',
    },
    {
      quando: 'Sei della Direzione e non trovi «Nuova scheda utente».',
      cosaFare: 'La Direzione non crea e non modifica le schede: serve anche un incarico di Operatore interno '
        + 'sulla sede.',
    },
  ],
  vediAnche: ['beneficiari', 'scheda', 'consensi', 'anonimizzate'],
  parole: ['creare', 'inserire', 'anagrafica', 'doppione', 'completa', 'mancano', 'obbligatori', 'm03', 'verificare',
    'documento', 'passaporto', 'permesso di soggiorno', 'leggere', 'ai', 'intelligenza artificiale', 'mrz', 'pdf'],
}

export default capitolo
